import React from 'react';
import { Layers, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BaseTooltip } from '@/components';
import type { LegendSyncMode } from '@/modules/AssetsStudioPage/SensorFusion/hooks';

interface LegendModeToggleProps {
  mode: LegendSyncMode;
  onToggle: () => void;
  className?: string;
  disabled?: boolean;
}

/**
 * LegendModeToggle - Toggle between focus and multi-select legend modes
 *
 * - Focus mode: Only one sensor visible across all sections
 * - Multi-select mode: Full sync with multiple sensors visible
 */
export const LegendModeToggle: React.FC<LegendModeToggleProps> = ({
  mode,
  onToggle,
  className = '',
  disabled = false,
}) => {
  const isFocus = mode === 'focus';

  // Tooltip shows what the button does
  const tooltipContent = isFocus
    ? 'Focus Mode Active: Click to exit and show all sensors'
    : 'Multi-Select Mode: Click to enable Focus mode (show one sensor at a time)';

  const handleClick = () => {
    onToggle();
  };

  return (
    <BaseTooltip content={tooltipContent}>
      <Button
        onClick={handleClick}
        disabled={disabled}
        variant="outline"
        size="sm"
        className={`flex items-center justify-center gap-2 w-9 h-9 p-0 rounded-full transition-all duration-200 ${
          isFocus
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30'
            : 'bg-gray-50 dark:bg-gray-900/20 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-900/30'
        } ${className}`}
      >
        {isFocus ? (
          <EyeOff className="w-4 h-4" />
        ) : (
          <Layers className="w-4 h-4" />
        )}
      </Button>
    </BaseTooltip>
  );
};
